import { startSsoRedirect } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback } from "react";

type SsoMode = "off" | "test" | "on";

interface UseSsoLoginResult {
  ssoMode: SsoMode;
  handleSsoRedirect: (source: "sso_button" | "submit") => Promise<void>;
}

export function useSsoLogin(email: string): UseSsoLoginResult {
  const ssoFlagValue = useCbhFlag(CbhFeatureFlag.ADMIN_APP_RIPPLING_SSO_ROLLOUT, {
    defaultValue: "off" as const,
  });

  // Non-aliased Clipboard Health emails (no + suffix) can use SSO.
  const isCbhEmployeeEmail = /^[^+]+@clipboardhealth\.com$/i.test(email.trim());
  // "off" = no SSO, "test" = both magic link + SSO button, "on" = SSO replaces magic link
  const ssoMode: SsoMode = ssoFlagValue !== "off" && isCbhEmployeeEmail ? ssoFlagValue : "off";

  const handleSsoRedirect = useCallback(async (source: "sso_button" | "submit") => {
    try {
      const requestedURL = localStorage.getItem("redirectURL") ?? undefined;
      await startSsoRedirect(requestedURL, {
        ssoMode: source === "submit" ? "on" : "test",
        source,
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.RIPPLING_SSO_REDIRECT_FAILURE, { error });
      showErrorToast("Unable to start SSO sign-in. Please try again.");
    }
  }, []);

  return { ssoMode, handleSsoRedirect };
}
